import { Link, useLocation, Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { authService } from "@/api";
import { useAuth } from "@/hooks/useAuth";
import { navigationItems, footerNavigationItems } from "@/utils/routeAccess";
import {
  Heart,
  User as UserIcon,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import UserProfileCard from "@/components/layout/UserProfileCard";
import BuildInfo from "@/components/layout/BuildInfo";

const CustomSidebarToggle = () => {
  const { open, toggleSidebar } = useSidebar();

  return (
    <button
      onClick={toggleSidebar}
      className="fixed z-50 transition-all duration-300 ease-in-out group hidden md:block"
      style={{
        left: open ? "15rem" : "0rem",
        top: "1rem",
      }}
    >
      <div className="w-8 h-14 bg-white border border-slate-300 rounded-r-lg shadow-xl flex items-center justify-center hover:bg-blue-50 transition-all duration-200 group-hover:scale-105 group-hover:shadow-2xl">
        {open ? (
          <ChevronLeft className="w-5 h-5 text-slate-800 group-hover:text-blue-600" />
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-800 group-hover:text-blue-600" />
        )}
      </div>
    </button>
  );
};

export default function Layout({ children }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const {
    user: currentUser,
    isLoading,
    error,
    navigationPermissions,
    hasAnyPermission,
  } = useAuth();

  // Redirect to login if there's an error loading user or user is not authenticated
  if (!isLoading && (!currentUser || error)) {
    return <Navigate to="/login" replace />;
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg mx-auto mb-4">
            <Heart className="w-8 h-8 text-white animate-pulse" />
          </div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await authService.logout();
      queryClient.invalidateQueries(["currentUser"]);
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  // Filter navigation items based on user permissions from backend
  const visibleNavigationItems = navigationPermissions
    ? navigationItems.filter((item) => {
        const requiredPermissions = navigationPermissions[item.navigationItem];
        return requiredPermissions && hasAnyPermission(requiredPermissions);
      })
    : [];

  const visibleFooterItems = navigationPermissions
    ? footerNavigationItems.filter((item) => {
        const requiredPermissions = navigationPermissions[item.title];
        return requiredPermissions && hasAnyPermission(requiredPermissions);
      })
    : [];

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Sidebar className="border-r border-slate-200/60 bg-white/80 backdrop-blur-xl">
          <SidebarHeader className="border-b border-slate-200/60 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-slate-900 text-lg">ClubMed</h2>
                <p className="text-xs text-slate-500 font-medium">
                  Medical Analytics Platform
                </p>
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent className="p-3">
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-3">
                Navigation
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {visibleNavigationItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        className={`hover:bg-blue-50/80 hover:text-blue-700 transition-all duration-300 rounded-xl mb-1 group ${
                          location.pathname === item.route
                            ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm border border-blue-100"
                            : ""
                        }`}
                      >
                        <Link
                          to={item.route}
                          className="flex items-center gap-3 px-4 py-3"
                        >
                          <item.icon
                            className={`w-5 h-5 ${item.color} group-hover:scale-110 transition-transform duration-200`}
                          />
                          <span className="font-medium">{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-slate-200/60 p-4">
            {/* System navigation group */}
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-3">
                System
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {visibleFooterItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        className={`hover:bg-blue-50/80 hover:text-blue-700 transition-all duration-300 rounded-xl mb-1 group ${
                          location.pathname === item.route
                            ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 shadow-sm border border-blue-100"
                            : ""
                        }`}
                      >
                        <Link
                          to={item.route}
                          className="flex items-center gap-3 px-4 py-3"
                        >
                          <item.icon
                            className={`w-5 h-5 ${item.color} group-hover:scale-110 transition-transform duration-200`}
                          />
                          <span className="font-medium">{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {/* User Profile Section with Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <div data-testid="user-profile-trigger" className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-100 cursor-pointer hover:bg-slate-100/50 rounded-lg p-2 -mx-2 transition-colors">
                  <div className="w-10 h-10 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center">
                    <UserIcon className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">
                      {currentUser?.name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {currentUser?.email || "Healthcare Provider"}
                    </p>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-72" side="top" align="end">
                <UserProfileCard user={currentUser} onLogout={handleLogout} />
              </PopoverContent>
            </Popover>
          </SidebarFooter>
        </Sidebar>

        <CustomSidebarToggle />

        <main className="flex-1 flex flex-col">
          <header className="bg-white/60 backdrop-blur-xl border-b border-slate-200/60 px-6 py-4 md:hidden">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="hover:bg-slate-100 p-2 rounded-lg transition-colors duration-200" />
              <h1 className="text-xl font-bold text-slate-900">ClubMed</h1>
            </div>
          </header>

          <div className="flex-1">{children}</div>
        </main>

        <BuildInfo />
      </div>
    </SidebarProvider>
  );
}
